import { observable, action, autorun } from 'mobx';
import UserAdapter from 'adapters/UserAdapter';
import errorHandler from 'services/ErrorHandler';

import UserNotifications from './UserNotifications';
import Parameters from './Parameters';
import paths from '../common/constants/paths';

let getSessionDataPromise = null;

class UserStore {
  @observable isInfoLoaded = false;
  @observable isAuthenticated = false;
  @observable email = null;
  @observable questionLoading = false;
  @observable allowedAreas = [ 'public' ];

  @observable notifications = null;
  @observable parameters = {};

  @observable subscribeCost = null;
  @observable subscribeUserId = null;
  @observable isRegisterInProgress = false;
  @observable isLogginInInprogress = false;
  @observable isUserDetailsUpdateInProgress = false;

  restrictedMode = false;

  get homePage() {
    if (this.isAuthenticated) {
      return this.restrictedMode ? '/my-account/details' : paths.dataSelect;
    } else {
      return paths.home;
    }
  }

  constructor() {
    this.updateSessionData();
    this.resetAuthStatus = this.resetAuthStatus.bind(this);

    this.notifications = new UserNotifications();
    this.parameters = new Parameters();

    autorun(() => {
      if (401 === errorHandler.lastErrorCode) {
        this.resetAuthStatus();
      }
    });
  }

  onLoggingInStart() {
    this.isLogginInInprogress = true;
  }

  onLoggingInEnd() {
    this.isLogginInInprogress = false;
  }

  updateSessionData() {
    this.isInfoLoaded = false;

    getSessionDataPromise = UserAdapter.getInfo();
    getSessionDataPromise.then((data) => {
      this.email = data.email;
      this.notifications.update(data.notifications);
      this.parameters.update(data.parameters);
      this.restrictedMode = data.restrictedMode;

      this.allowedAreas = [ 'public', 'myAccount' ];

      if (!data.restrictedMode) {
        this.allowedAreas.push('dataViewing');
      }

      this.isAuthenticated = true;
      this.isInfoLoaded = true;
    }, this.resetAuthStatus);
  }

  @action
  register(userData, venueRegisters) {
    this.isRegisterInProgress = true;
    return UserAdapter.register(userData, venueRegisters).then(
      action((res) => {
        this.setSubscribeInfo(res.subscribeUserId, res.subscribeCost);
        this.isRegisterInProgress = false;
      }),
      action((error) => {
        this.isRegisterInProgress = false;
      })
    );
  }

  resetAuthStatus = action(() => {
    this.isAuthenticated = false;
    this.isInfoLoaded = true;
    this.email = null;
  });

  logout() {
    return UserAdapter.logout();
  }

  @action
  setSubscribeInfo(userId, cost) {
    this.subscribeUserId = userId;
    this.subscribeCost = cost;
  }

  @action
  clearSubscribeInfo() {
    this.subscribeUserId = null;
    this.subscribeCost = null;
  }

  @action
  sendRestorePasswordEmail(email) {
    this.questionLoading = true;
    return UserAdapter.sendRestorePasswordEmail(email)
      .then((result) => {
        this.questionLoading = false;
        return result;
      })
      .catch((e) => {
        this.questionLoading = false;
        throw e;
      });
  }

  @action
  getQuestion(token) {
    this.questionLoading = true;
    return UserAdapter.getQuestion(token)
      .then((result) => {
        this.questionLoading = false;
        return result;
      })
      .catch((e) => {
        this.questionLoading = false;
        throw e;
      });
  }

  @action
  restorePassword(token, answer, newPassword) {
    return UserAdapter.restorePassword(token, answer, newPassword);
  }

  @action
  changePassword(currentPassword, newPassword) {
    return UserAdapter.changePassword(currentPassword, newPassword);
  }

  getUserDetails() {
    this.isUserDetailsUpdateInProgress = true;
    return UserAdapter.getUserDetails()
      .then((result) => {
        this.isUserDetailsUpdateInProgress = false;
        return result;
      })
      .catch((e) => {
        this.isUserDetailsUpdateInProgress = false;
        throw e;
      });
  }

  saveUserDetails(userDetails) {
    this.isUserDetailsUpdateInProgress = true;
    return UserAdapter.saveUserDetails(userDetails)
      .then((result) => {
        this.isUserDetailsUpdateInProgress = false;
        return result;
      })
      .catch((e) => {
        this.isUserDetailsUpdateInProgress = false;
        throw e;
      });
  }
}

const userStore = new UserStore();

export default userStore;



// WEBPACK FOOTER //
// ./src/stores/UserStore.js